import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon, PartyPopper, Sparkles, CalendarDays } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useNavigate } from "react-router-dom";

const MODES = {
  table: {
    title: "Reserve a table",
    description: "A few quick details and you're on the books. We'll email your confirmation.",
    submit: "Book Table",
    icon: CalendarDays,
  },
  group: {
    title: "Birthday? Group celebration?",
    description: "Hold the table, the room, and the vibe. We'll call to confirm details.",
    submit: "Send Group RSVP",
    icon: PartyPopper,
  },
  event: {
    title: "RSVP for this event",
    description: "We'll save your spot and ring you with anything we need.",
    submit: "RSVP Now",
    icon: CalendarDays,
  },
  vip: {
    title: "Book the VIP Room",
    description: "Private lounge for birthdays, anniversaries, and grown-folk vibes.",
    submit: "Request VIP Room",
    icon: Sparkles,
  },
};

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  party_size: "2",
  time: "",
  notes: "",
  occasion: "",
  group_name: "",
};

export default function ReservationDialog({
  open,
  onOpenChange,
  mode = "group", // "table" | "group" | "event" | "vip"
  event = null, // { id, title, event_date, event_time }
  initial = null, // { date: Date, time: string, party_size: number|string }
}) {
  const [form, setForm] = useState(EMPTY);
  const [date, setDate] = useState(null);
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const cfg = MODES[mode] || MODES.group;
  const Icon = cfg.icon;
  const isEvent = mode === "event" && event;

  useEffect(() => {
    if (open) {
      const base = { ...EMPTY };
      if (initial?.party_size) base.party_size = String(initial.party_size);
      if (initial?.time) base.time = initial.time;
      setForm(base);
      if (isEvent && event?.event_date) {
        setDate(new Date(event.event_date + "T00:00:00"));
      } else if (initial?.date) {
        setDate(initial.date);
      } else {
        setDate(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!date) {
      toast.error("Pick a date");
      return;
    }
    setSubmitting(true);
    try {
      const { getReferralCode } = await import("@/lib/referral");
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        party_size: parseInt(form.party_size, 10) || 1,
        date: format(date, "yyyy-MM-dd"),
        time: isEvent ? event?.event_time || null : form.time || null,
        occasion: form.occasion || (mode === "group" ? "Group celebration" : null),
        notes: form.notes || null,
        booking_type: mode,
        group_name: form.group_name || null,
        event_id: isEvent ? event.id : null,
        event_title: isEvent ? event.title : null,
        referrer_code: getReferralCode() || null,
      };
      const { data } = await api.post("/reservations", payload);
      toast.success(
        mode === "event"
          ? "You're on the list — we'll be in touch."
          : mode === "table"
          ? "You're on the books! Confirmation sent to your email."
          : "Got it. We'll call to confirm your party."
      );
      onOpenChange(false);
      navigate(`/rsvp/${data.id}?new=1`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`reservation-dialog-${mode}`}
        className="bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none max-w-lg max-h-[92vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[var(--trio-primary)] font-bold">
            <Icon className="w-3.5 h-3.5" />
            {mode === "event" ? "Event RSVP" : mode === "vip" ? "VIP Room" : mode === "table" ? "Quick Book" : "Group RSVP"}
          </div>
          <DialogTitle className="font-display uppercase text-3xl tracking-tight leading-tight">
            {cfg.title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--trio-muted)]">
            {cfg.description}
          </DialogDescription>
          {isEvent && (
            <div className="mt-3 bg-[var(--trio-surface)] border border-[var(--trio-border)] p-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--trio-accent)] font-bold">
                {event.event_date}
                {event.event_time ? ` · ${event.event_time}` : ""}
              </div>
              <div className="font-display uppercase text-lg tracking-tight">
                {event.title}
              </div>
            </div>
          )}
        </DialogHeader>

        <form onSubmit={onSubmit} className="space-y-4" data-testid="reservation-dialog-form">
          {mode === "group" && (
            <Row
              label="Group / celebrant name"
              value={form.group_name}
              onChange={(v) => update("group_name", v)}
              testid="rd-group-name"
              placeholder="e.g. Jasmine's 30th"
            />
          )}
          <Row
            label="Your full name"
            value={form.name}
            onChange={(v) => update("name", v)}
            required
            testid="rd-name"
          />
          <Row
            label="Email"
            type="email"
            value={form.email}
            onChange={(v) => update("email", v)}
            required
            testid="rd-email"
            placeholder="We'll send your confirmation here"
          />
          <Row
            label="Phone"
            value={form.phone}
            onChange={(v) => update("phone", v)}
            required
            testid="rd-phone"
            placeholder="So we can call or text to confirm"
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs tracking-widest uppercase">Date</Label>
              {isEvent ? (
                <div className="mt-2 bg-[var(--trio-surface)] border border-[var(--trio-border)] px-3 py-2 text-sm">
                  {date ? format(date, "EEE, MMM d, yyyy") : "—"}
                </div>
              ) : (
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="rd-date-trigger"
                      className="mt-2 w-full flex items-center justify-between bg-[var(--trio-bg)] border border-[var(--trio-border)] px-3 py-2 text-sm hover:border-[var(--trio-text)]"
                    >
                      {date ? format(date, "EEE, MMM d, yyyy") : "Choose a date"}
                      <CalendarIcon className="w-4 h-4 opacity-60" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0 bg-[var(--trio-bg)] border-[var(--trio-border)] rounded-none"
                    align="start"
                  >
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <Row
              label="How many people"
              type="number"
              value={form.party_size}
              onChange={(v) => update("party_size", v)}
              required
              testid="rd-party-size"
            />
          </div>

          {mode === "table" && (
            <Row
              label="Time"
              value={form.time}
              onChange={(v) => update("time", v)}
              required
              testid="rd-time"
              placeholder="e.g. 7:30 PM"
            />
          )}

          {mode !== "event" && (
            <Row
              label="Occasion (optional)"
              value={form.occasion}
              onChange={(v) => update("occasion", v)}
              placeholder="Birthday, anniversary, just because…"
              testid="rd-occasion"
            />
          )}

          <div>
            <Label className="text-xs tracking-widest uppercase">Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
              placeholder="Anything we should know? Allergies, decorations, cake drop-off, surprise plan..."
              data-testid="rd-notes"
              className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="rd-submit"
            className="w-full bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-3 text-xs tracking-widest uppercase font-bold disabled:opacity-60 transition-colors"
          >
            {submitting ? "Sending…" : cfg.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <Label className="text-xs tracking-widest uppercase">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        min={type === "number" ? 1 : undefined}
        className="mt-2 rounded-none bg-[var(--trio-bg)] border-[var(--trio-border)] focus-visible:ring-[var(--trio-primary)]"
      />
    </div>
  );
}
